import { Stack } from '@mui/material';
import SettingsKey from 'app/profile/settings/utils/settingsTypes';
import FormNav from 'common/components/FormNav';
import useFlexBoxStyles from 'common/flexBox';
import LayoutForm from 'common/layouts/LayoutForm';
import { useFormFields } from 'libs/hooksLib';
import { isEqual } from 'lodash';
import { useSnackbar } from 'notistack';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { cancelCloseCustomDialogAction } from 'redux/slices/customDialogSlice';
import { getFormsSavePending } from 'redux/slices/formSlice';
import ViewerExtraFieldsForm from '../../profile/settings/ViewerExtraField/ViewerExtraFieldsForm';
import FormFormContext from './FormFormContext';
import FormFormSettings from './FormFormSettings';

const FormForm = ({ form, action, onSubmit, close }) => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const cancelConfirmTitle = t('common.cancelConfirm');
    const cancelConfirmMessage = t('form.formForm.cancelMessage');
    const [openCancelDlg, setOpenCancelDlg] = useState(false);
    const { classes: flexBox, cx } = useFlexBoxStyles();
    const savePending = useSelector(getFormsSavePending);
    const initialValues = useMemo(
        () => ({
            name: form.name || '',
            description: form.description || '',
            fields: form.fields || [],
            textActionSubmitJoinForm: form.textActionSubmitJoinForm || [{ languageCode: 'en', value: '' }],
            context: form.context || null,
            organizationId: form.organizationId || null,
            specifyContext: !!form.context,
        }),
        [form.name, form.description, form.fields, form.textActionSubmitJoinForm, form.context, form.organizationId],
    );

    const [fields, handleFieldChange] = useFormFields(initialValues);
    const [step, setStep] = useState(1);

    const visibleSteps = [
        t('form.form.settingSection'),
        ...(fields.specifyContext ? [t('form.formForm.specifyContextSection')] : []),
        t('form.form.fieldSettings'),
    ];

    const handleSubmit = () => {
        onSubmit({
            data: {
                ...fields,
            },
            onSuccess: () => {
                enqueueSnackbar(form?.id ? t('form.formForm.updateSuccess') : t('form.formForm.createSuccess'), {
                    variant: 'success',
                });
            },
            onFailure: (error) => {
                if (error) {
                    enqueueSnackbar(error.response?.data?.description || t('app.common.error'), {
                        variant: 'error',
                    });
                }
            },
        });
    };

    const finalCancel = () => {
        close();
    };

    const cancelDlg = () => {
        setOpenCancelDlg(false);
        dispatch(cancelCloseCustomDialogAction());
    };

    const userDidChange = useCallback(() => {
        return (
            !isEqual(fields.eventType, initialValues.name) ||
            !isEqual(fields.description, initialValues.description) ||
            !isEqual(fields.fields, initialValues.fields) ||
            !isEqual(fields.textActionSubmitJoinForm, initialValues.textActionSubmitJoinForm) ||
            !isEqual(fields.context, initialValues.context)
        );
    }, [fields, initialValues]);

    const handleCancel = () => {
        if (!userDidChange()) {
            finalCancel();
        }
        setOpenCancelDlg(true);
    };

    const handleError = () => {
        enqueueSnackbar(t('app.common.formError'), {
            variant: 'error',
        });
    };

    const toggleChange = (settingKey, settingValue) => {
        if (settingKey === SettingsKey.VIEWER_EXTRA_FIELDS) {
            handleFieldChange({
                target: {
                    value: settingValue,
                    name: 'fields',
                },
            });
        }
    };

    const renderFormContext = () => (
        <FormFormContext
            fields={fields}
            handleFieldChange={handleFieldChange}
            step={step}
            setStep={(s) => setStep(s)}
            cancel={handleCancel}
            handleError={handleError}
        />
    );
    const renderExtraFields = () => (
        <ViewerExtraFieldsForm
            viewerExtraField={fields.fields}
            toggleChange={toggleChange}
            handleSubmit={handleSubmit}
            cancel={handleCancel}
            savePending={savePending}
            handleError={handleError}
            context={fields.context}
            id={form.id}
            organizationId={form.organizationId}
        />
    );

    return (
        <LayoutForm
            title={action}
            confirmDlg={{
                open: openCancelDlg,
                accept: finalCancel,
                cancel: cancelDlg,
                titleDlg: cancelConfirmTitle,
                messageDlg: cancelConfirmMessage,
            }}
        >
            {/*{!subForm && ()}*/}
            <Stack direction="column" spacing={3} className={flexBox.overflowHidden}>
                <FormNav step={step} setStep={(s) => setStep(s)} steps={visibleSteps} wizardMode={true} />
                <Stack direction="column" className={cx(flexBox.overflowAuto, flexBox.scrollbarNone, flexBox.h100)}>
                    {
                        {
                            1: (
                                <FormFormSettings
                                    fields={fields}
                                    handleFieldChange={handleFieldChange}
                                    step={step}
                                    setStep={(s) => setStep(s)}
                                    cancel={handleCancel}
                                    handleError={handleError}
                                />
                            ),
                            ...(fields.specifyContext
                                ? {
                                    2: renderFormContext(),
                                    3: renderExtraFields(),
                                }
                                : {
                                    2: renderExtraFields(),
                                }),
                        }[step]
                    }
                </Stack>
            </Stack>
        </LayoutForm>
    );
};

export default FormForm;
